import React, { useContext, useEffect, useState } from "react";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";
import SendTemplateFromTicketModal from "../SendTemplateFromTicketModal";

// Formata um intervalo em ms como "2h 47min" / "47min" / "<1min"
const fmtRemaining = (ms) => {
  if (ms <= 0) return "0min";
  const totalMin = Math.floor(ms / 60000);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  if (h > 0) return `${h}h ${m.toString().padStart(2, "0")}min`;
  if (m > 0) return `${m}min`;
  return "<1min";
};

/**
 * Indicador da janela de atendimento de 24h (WhatsApp Business API).
 * Só aparece em tickets de conexão oficial (waba). Em chip não-oficial
 * (wwebjs) não renderiza nada.
 */
const WabaWindowIndicator = ({ ticket }) => {
  const { user } = useContext(AuthContext);
  const [data, setData] = useState(null);
  const [now, setNow] = useState(Date.now());
  const [modalOpen, setModalOpen] = useState(false);

  const profile = (user?.profile || "").toLowerCase();
  const canSend =
    profile === "admin" ||
    profile === "masteradmin" ||
    Boolean(user?.allowSendTemplate);

  const ticketId = ticket?.id;
  const wppType = ticket?.whatsapp?.type;

  // Se sabemos que é não-oficial, nem busca.
  const skip = !ticketId || (wppType && wppType !== "waba");

  // Busca o estado da janela no backend (recalcula tempo no cliente)
  useEffect(() => {
    if (skip) {
      setData(null);
      return undefined;
    }
    let active = true;
    const fetchWindow = async () => {
      try {
        const { data: res } = await api.get(`/tickets/${ticketId}/waba-window`);
        if (active) setData(res);
      } catch (err) {
        if (active) setData(null);
      }
    };
    fetchWindow();
    // Refaz a busca a cada 60s (captura renovação da janela por nova msg do cliente)
    const refetch = setInterval(fetchWindow, 60000);
    return () => {
      active = false;
      clearInterval(refetch);
    };
  }, [ticketId, skip]);

  // Atualiza o relógio a cada 30s para o countdown "andar"
  useEffect(() => {
    const tick = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(tick);
  }, []);

  if (skip || !data || !data.official || !data.enabled) {
    return null;
  }

  const windowMs = (data.windowHours || 24) * 3600000;
  const alertMs = (data.alertHours || 3) * 3600000;

  let color = "default";
  let label = "";
  let tip = "";
  let closed = false;

  if (!data.lastClientMessageAt) {
    color = "default";
    label = "Aguardando cliente";
    tip =
      "A janela de 24h abre quando o cliente enviar a primeira mensagem. Antes disso, só é possível iniciar com um template aprovado.";
  } else {
    const expires = new Date(data.lastClientMessageAt).getTime() + windowMs;
    const remaining = expires - now;

    if (remaining <= 0) {
      closed = true;
      color = "error";
      label = "Janela fechada";
      tip =
        "Passaram-se 24h desde a última mensagem do cliente. Para falar agora, envie um template aprovado (a resposta dele reabre a janela).";
    } else if (remaining <= alertMs) {
      color = "warning";
      label = `Fecha em ${fmtRemaining(remaining)}`;
      tip =
        "A janela de 24h está prestes a fechar. Responda agora — depois disso, só com template aprovado.";
    } else {
      color = "success";
      label = "Janela aberta";
      tip = `Janela de 24h aberta — você pode responder livremente. Fecha em ${fmtRemaining(
        remaining
      )}.`;
    }
  }

  const clickable = closed && canSend;
  const finalTip = clickable
    ? `${tip} Clique para enviar um template aprovado.`
    : tip;

  return (
    <>
      <Tooltip title={finalTip} arrow placement="top">
        <Chip
          icon={<AccessTimeIcon fontSize="small" />}
          label={clickable ? `${label} • enviar template` : label}
          size="small"
          color={color}
          variant={color === "default" ? "outlined" : "filled"}
          onClick={clickable ? () => setModalOpen(true) : undefined}
          sx={{
            ml: 0.5,
            height: 22,
            cursor: clickable ? "pointer" : "default",
            "& .MuiChip-label": { fontSize: "0.72rem" },
          }}
        />
      </Tooltip>
      {clickable && (
        <SendTemplateFromTicketModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          ticket={ticket}
        />
      )}
    </>
  );
};

export default WabaWindowIndicator;
